import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {Actions} from '@ngrx/effects';
import {BehaviorSubject, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';

import {AimoRoutingService} from '../../../cms-structure/routing/aimo-routing.service';
import {AimoCart, AimoCartItemComponentOptions} from '../../../model/cart.model';
import {AimoProduct} from '../../../model/product.model';
import {AimoActiveCartService} from '../../../service/cart/aimo-active-cart.service';
import {AimoGTMProductAttributes} from '../../../service/gtm/aimo-gtm.model';
import {AimoProductService} from '../../../service/product/aimo-product.service';
import {ProductEvent} from '../../../shared/item-counter/aimo-item-counter.component';
import {AimoCurrentProductService} from "../../../service/product/aimo-current-product.service";

@Component({
    selector: 'aimo-cx-add-to-cart',
    templateUrl: './aimo-add-to-cart.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AimoAddToCartComponent implements OnInit, OnDestroy {
    @Input() product: AimoProduct;
    @Input() options: AimoCartItemComponentOptions;
    @Input() gtmProductAttributes?: AimoGTMProductAttributes;
    @Input() idSuffix = '';

    subscriptions: Subscription = new Subscription();

    maxQuantity: number;
    purchasable: boolean;
    quantityStep: number;
    @Input() showQuantity: boolean;
    @Input() showAvailabilityNotification: boolean = false;

    addToCartForm: UntypedFormGroup;
    @Input() isPLP = false;
    @Output() showHideStepper = new EventEmitter<boolean>();
    @Output() afterChange = new EventEmitter<number>();
    @Input() addToCartCallBack: BehaviorSubject<number> = new BehaviorSubject(undefined);
    @Input() autofocus: boolean;

    constructor(
        protected currentProductService: AimoCurrentProductService,
        protected cd: ChangeDetectorRef,
        protected activeCartService: AimoActiveCartService,
        protected actions$: Actions,
        protected aimoRoutingService: AimoRoutingService,
        protected productService: AimoProductService,
    ) {
        this.createFormGroup(0);
    }

    ngOnInit(): void {
        this.product = {...this.product} as AimoProduct;
        this.purchasable = this.product.purchasable;
        this.quantityStep = this.product.allowedLotSize;
        this.maxQuantity = this.product.stock.futureStockLevel
            ? this.product.stock.futureStockLevel
            : this.product.stock.stockLevel;

        this.subscriptions.add(
            this.activeCartService
                .getActive()
                .pipe(
                    filter((cart) => cart.code !== undefined),
                    map(cart => this.calculateExistingQty(cart)),
                    distinctUntilChanged((prev, curr) => prev === curr),
                )
                .subscribe((existingQuantity: number) => this.initQuantityForm(existingQuantity))
        );
    }

    private calculateExistingQty(cart: AimoCart): number {
        return cart.dayGroupedEntries
            ?.filter((d) => d.active)
            ?.flatMap((d) => d.entries)
            ?.filter((e) => e.product.code === this.product.code)
            .map((e) => e.quantity)
            .find((e) => e);
    }

    private initQuantityForm(existingQuantity: number): void {
        this.createFormGroup(existingQuantity);
        this.cd.detectChanges();
    }

    private createFormGroup(alreadyInCart: number): void {
        this.addToCartForm = new UntypedFormGroup({
            quantity: new UntypedFormControl(alreadyInCart ? alreadyInCart : 0, {updateOn: 'blur'}),
        });
    }

    addToCart(): void {
        this.updateCart();
    }

    updateCart(event$?: ProductEvent): void {
        const quantity = this.addToCartForm.get('quantity')?.value;
        if (!this.product.code) {
            return;
        }
        this.activeCartService.addAimoEntry(this.product.code, quantity, this.gtmProductAttributes);
        if (event$) {
            this.showHideStepper.emit(event$.showHideQuantity);
        }
        this.afterChange.next(quantity);
    }

    isPikatukku(): boolean {
        return this.aimoRoutingService.isPikatukku();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    toggleAvailabilityNotification(): void {
        this.productService.toggleAvailabilityNotificationMessage(this.product);
    }
}
